<template>
  <div style="display: contents">
    <div
      class="column-settings-panel"
      style="display: flex; flex-direction: column; height: 100%"
    >
      <h1 class="title is-size-5">Column settings</h1>
      <div
        class="pl-5 pr-5 pb-5 mb-5"
        style="
          display: flex;
          flex-basis: 0;
          flex-grow: 1;
          overflow-x: hidden;
          overflow-y: auto;
        "
      >
        <div
          class="pl-3 pr-3"
          style="
            display: flex;
            flex-direction: column;
            height: 100%;
            position: sticky;
            top: 0;
            min-height: 200px;
          "
        >
          <div class="p-1">
            <p>Visible columns</p>
            <p class="is-size-7 pt-1">Drag & drop to reorganise</p>
          </div>
          <div
            :class="{
              [`${visibleContainerId}-wrapper`]: true,
              'enlight-container': isOrderingVisible || isAddingColumn,
            }"
            class="has-background-white"
            style="
              display: flex;
              flex-grow: 1;
              flex-shrink: 1;
              border: 1px solid grey;
              overflow-y: auto;
              overflow-x: hidden;
              height: 100%;
            "
          >
            <SelectableRows
              :rows="visibleColumns"
              @selected-indices="(i) => (selectedVisibleIndices = i)"
            >
              <template #default="{ selectableRows, toggleSelectRow }">
                <ul
                  :id="visibleContainerId"
                  class="sortable-items"
                  style="height: 100%; width: 100%; max-width: 240px"
                >
                  <li
                    v-for="({ item, selected }, index) in selectableRows"
                    :key="`vis-${item.id}`"
                    class="li-item sortable p-1"
                    :class="{ 'is-selected': selected }"
                    @click="toggleSelectRow(index, $event.shiftKey)"
                  >
                    <div class="is-flex is-align-items-center">
                      <div
                        class="draggable-handle is-flex is-align-items-center is-flex-shrink-1 is-flex-grow-1"
                        style="min-width: 0"
                      >
                        <span class="icon sortable-handle is-flex-shrink-0">
                          <i class="fa-solid fa-arrows-alt-v"></i>
                        </span>
                        <span
                          v-if="item.category === 'annotation'"
                          class="icon is-small pr-1 is-flex-shrink-0 has-text-primary"
                        >
                          <i class="fa-solid fa-tag fa-xs" />
                        </span>
                        <span
                          class="is-flex is-align-items-center handle-text is-flex-shrink-1 is-flex-grow-1"
                          style="overflow-x: hidden"
                        >
                          <span
                            class="long-text-ellipsis is-flex-shrink-1"
                            style="min-width: 0"
                          >
                            {{ item.label }}
                          </span>
                          <HelpIcon
                            :label="item.label"
                            :text="helpText(item)"
                          />
                        </span>
                      </div>
                      <div class="ml-2">
                        <div class="icons is-flex is-align-items-center">
                          <span
                            class="icon has-text-primary hoverable-icon"
                            @click.stop="
                              $emit('move-columns', {
                                fromIndices: [index],
                                toIndex: 0,
                              })
                            "
                          >
                            <i
                              class="fa-solid fa-step-backward fa-sm fa-rotate-90"
                            ></i>
                          </span>
                          <span
                            class="icon hoverable-icon"
                            @click.stop="$emit('remove-column', { index })"
                          >
                            <i class="fa fa-times fa-sm" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </template>
            </SelectableRows>
          </div>
          <button
            v-if="handleSaveSettings"
            class="button is-small is-outlined is-primary"
            tabindex="0"
            @click="handleSaveSettings"
            @keyup.enter="handleSaveSettings"
          >
            <span class="icon">
              <i class="fa-solid fa-save" />
            </span>
            <span>Save</span>
          </button>
        </div>
        <div
          class="pl-3 pr-3"
          style="
            display: flex;
            position: sticky;
            top: 0;
            height: 100%;
            align-items: center;
          "
        >
          <span class="icon" :class="{ 'has-text-primary': isAddingColumn }">
            <i
              class="fa fa-caret-left fa-2x"
              :class="{ 'fa-4x': isAddingColumn }"
            />
          </span>
        </div>
        <div
          class="is-flex is-flex-direction-column pl-3 pr-3"
          style="overflow: hidden; min-height: 400px"
        >
          <div
            class="is-flex is-flex-direction-column pb-2"
            style="flex-basis: 50%; flex-grow: 1; overflow: hidden"
          >
            <div class="is-flex is-justify-content-space-between p-1">
              <div>Attributes</div>
              <a
                v-if="nonVisibleAttributes.length > 0"
                class="is-flex is-align-items-center is-size-6"
                event
                @click.prevent.stop="
                  addColumns(
                    nonVisibleAttributes.map((a) => a.id),
                    undefined,
                    'attributes',
                  )
                "
              >
                <span class="pr-1">Add all</span>
                <span class="icon is-small clickable-icon hoverable-icon">
                  <i class="fa-solid fa-plus fa-xs"></i>
                </span>
              </a>
            </div>
            <InputTextFilter
              :key="`input-filt-attrs-init-${attrAddReact}`"
              init-input=""
              :can-change-mode="false"
              :case-insensitive="true"
              :show-filtered-count="true"
              :show-re-mode="false"
              :init-is-regex="false"
              placeholder="Filter..."
              :list-of-values="preFilterAttributes"
              @filter-list="
                (m) => (filteredAttributeIds = m.map(({ id }) => id))
              "
            ></InputTextFilter>
            <div
              class="has-background-white"
              :style="preventDropOfAnnotations"
              style="
                flex-grow: 1;
                border: 1px solid grey;
                overflow-y: auto;
                overflow-x: hidden;
              "
            >
              <SelectableRows
                :rows="nonVisibleAttributes"
                @selected-indices="
                  (indices) => (selectedAttributesIndices = indices)
                "
              >
                <template #default="{ selectableRows, toggleSelectRow }">
                  <ul
                    :id="attributeContainerId"
                    class="sortable-items"
                    :class="{
                      'blur-items': isAddingColumn,
                    }"
                    style="height: 100%; width: 100%"
                  >
                    <template
                      v-for="({ item, selected }, index) in selectableRows"
                      :key="`attrs-${item.id}`"
                    >
                      <li
                        v-show="filteredAttributeIds.includes(item.id)"
                        class="li-item sortable p-1"
                        :class="{ 'is-selected': selected }"
                        @click="
                          (event) => {
                            clickEvent = event
                            toggleSelectRow(index, event.shiftKey)
                          }
                        "
                      >
                        <div class="is-flex is-align-items-center">
                          <div
                            class="draggable-handle is-flex is-align-items-center is-flex-shrink-1 is-flex-grow-1"
                            style="flex-basis: 200px; min-width: 0"
                          >
                            <span class="icon sortable-handle">
                              <i class="fa-solid fa-grip-lines-vertical"></i>
                            </span>
                            <span class="handle-text long-text-ellipsis">
                              <span>
                                {{ item.label }}
                              </span>
                              <HelpIcon
                                :label="item.label"
                                :text="
                                  item.description !== 'Select one.'
                                    ? item.description
                                    : null
                                "
                              />
                            </span>
                          </div>
                          <div class="ml-2">
                            <div class="icons is-flex is-align-items-center">
                              <span
                                class="icon is-small has-text-primary clickable-icon hoverable-icon"
                                @click.prevent.stop="
                                  addColumns([item.id], undefined, 'attributes')
                                "
                              >
                                <i class="fa-solid fa-plus fa-sm"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </template>
                  </ul>
                </template>
              </SelectableRows>
            </div>
          </div>
          <div
            v-if="nonVisibleAnnotations.length > 0"
            class="is-flex is-flex-direction-column"
            style="flex-basis: 50%; flex-grow: 1; overflow: hidden"
          >
            <div class="is-flex is-justify-content-space-between p-1">
              <div>Annotations</div>
              <a
                v-tooltip="
                  `Add ${annotationsWithValue.length} ${pluralize(
                    'annotation',
                    annotationsWithValue.length,
                  )} where a value was found`
                "
                class="is-flex is-align-items-center is-size-6"
                :class="{ 'is-disabled': annotationsWithValue.length === 0 }"
                event
                @click.prevent.stop="
                  addColumns(
                    annotationsWithValue.map(
                      ({ annotation_type_id }) => annotation_type_id,
                    ),
                    undefined,
                    'annotations',
                  )
                "
              >
                <span class="pr-1">
                  Add ({{ annotationsWithValue.length }})
                </span>
                <span class="icon is-small clickable-icon hoverable-icon">
                  <i class="fa-solid fa-plus fa-xs"></i>
                </span>
              </a>
            </div>
            <InputTextFilter
              :key="`input-filt-anns-init-${annAddReact}`"
              :can-change-mode="false"
              :case-insensitive="true"
              :show-filtered-count="true"
              :show-re-mode="false"
              :init-is-regex="false"
              placeholder="Filter..."
              :list-of-values="preFilterAnnotations"
              @filter-list="
                (m) => (filteredAnnotationIds = m.map(({ id }) => id))
              "
            ></InputTextFilter>
            <div
              class="has-background-white"
              :style="preventDropOfAttributes"
              style="
                flex-grow: 1;
                border: 1px solid grey;
                overflow-y: auto;
                overflow-x: hidden;
              "
            >
              <SelectableRows
                :rows="nonVisibleAnnotations"
                @selected-indices="(i) => (selectedAnnotationsIndices = i)"
              >
                <template #default="{ selectableRows, toggleSelectRow }">
                  <ul
                    :id="annotationContainerId"
                    class="sortable-items"
                    :class="{
                      'blur-items': isAddingColumn,
                    }"
                    style="height: 100%; width: 100%"
                  >
                    <template
                      v-for="({ item, selected }, index) in selectableRows"
                      :key="`attrs-${item.id}`"
                    >
                      <li
                        v-show="filteredAnnotationIds.includes(item.id)"
                        class="li-item sortable p-1"
                        :class="{
                          'is-selected': selected,
                        }"
                        @click="
                          (event) => {
                            clickEvent = event
                            toggleSelectRow(index, event.shiftKey)
                          }
                        "
                      >
                        <div class="is-flex is-align-items-center">
                          <div
                            class="draggable-handle is-flex is-align-items-center is-flex-shrink-1 is-flex-grow-1"
                            style="flex-basis: 200px; min-width: 0"
                          >
                            <span class="icon sortable-handle">
                              <i class="fa-solid fa-grip-lines-vertical"></i>
                            </span>
                            <span
                              class="is-flex is-align-items-center handle-text"
                              style="overflow-x: hidden"
                            >
                              <span
                                class="long-text-ellipsis is-flex-shrink-1"
                                style="min-width: 0"
                              >
                                {{ item.label }}
                              </span>
                              <HelpIcon
                                :label="item.label"
                                :text="
                                  item.description !== 'Select one.'
                                    ? item.description
                                    : null
                                "
                              />
                            </span>
                          </div>
                          <div class="is-flex is-align-items-center">
                            <span
                              v-if="
                                annotationsWithValue
                                  .map(
                                    ({ annotation_type_id }) =>
                                      annotation_type_id,
                                  )
                                  .includes(item.id)
                              "
                              v-tooltip="
                                'A value was found for this annotations'
                              "
                              class="icon is-small has-text-primary ml-2"
                            >
                              <i class="fa-solid fa-circle fa-xs"></i>
                            </span>
                            <span
                              class="icon is-small has-text-primary clickable-icon hoverable-icon ml-2"
                              @click.prevent.stop="
                                addColumns([item.id], undefined, 'annotations')
                              "
                            >
                              <i class="fa-solid fa-plus fa-sm"></i>
                            </span>
                          </div>
                        </div>
                      </li>
                    </template>
                  </ul>
                </template>
              </SelectableRows>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import pluralize from 'pluralize'
import { Sortable } from '@shopify/draggable'
import InputTextFilter from '@/components/InputTextFilter.vue'
import SelectableRows from '@/components/SelectableRows.vue'
import HelpIcon from '@/components/ui/HelpIcon.vue'

const VISIBLE_CONTAINER_ID = 'container-visible-cols'
const ATTRIBUTES_CONTAINER_ID = 'container-attributes'
const ANNOTATIONS_CONTAINER_ID = 'container-annotations'

export default {
  name: 'DataTableColumnSettings',
  components: { InputTextFilter, SelectableRows, HelpIcon },
  props: {
    columnsData: {
      type: Object,
      default: () => {},
    },
    visibleColumnIds: {
      type: Array,
      default: () => [],
    },
    annotationsWithValue: {
      type: Array,
      default: () => [],
    },
    handleSaveSettings: {
      type: Function,
    },
  },
  data() {
    return {
      visibleContainerId: VISIBLE_CONTAINER_ID,
      attributeContainerId: ATTRIBUTES_CONTAINER_ID,
      annotationContainerId: ANNOTATIONS_CONTAINER_ID,
      sortable: null,
      selectedVisibleIndices: [],
      selectedAttributesIndices: [],
      selectedAnnotationsIndices: [],
      attrAddReact: 0,
      filteredAttributeIds: [],
      annAddReact: 0,
      filteredAnnotationIds: [],
      dragSourceId: null,
      dragSelection: false,
      lostOriginalSource: false,
      updateMirror: false,
      maxMirrorElem: 3,
      currentSortIndex: -1,
      clickEvent: null,
    }
  },
  computed: {
    columns() {
      return Object.values(this.columnsData).reduce(
        (cols, { category, id, label, name, fieldMeta }) => {
          const better_label = label || fieldMeta?.label
          const better_name = name || fieldMeta?.name
          const description = fieldMeta?.description
          const help = fieldMeta?.help
          cols[id] = {
            id,
            category,
            description,
            help,
            name: better_name,
            label: better_label,
          }
          return cols
        },
        {},
      )
    },
    visibleColumns() {
      return this.visibleColumnIds.map((id) => this.columns[id])
    },
    nonVisibleColumnIds() {
      return Object.keys(this.columns).reduce((nonVisCol, id) => {
        const isNotVisible = !this.visibleColumnIds.includes(id)
        if (isNotVisible) nonVisCol.push(id)
        return nonVisCol
      }, [])
    },
    nonVisibleAttributes() {
      return this.nonVisibleColumnIds.reduce((attrCols, id) => {
        const column = this.columns[id]
        if (column.category === 'attribute') attrCols.push(column)
        return attrCols
      }, [])
    },
    preFilterAttributes() {
      return this.nonVisibleAttributes.map(({ id, label }) => ({
        id,
        name: label,
        value: label,
      }))
    },
    nonVisibleAnnotations() {
      return this.nonVisibleColumnIds.reduce((annCols, id) => {
        const column = this.columns[id]
        if (column.category === 'annotation') annCols.push(column)
        return annCols
      }, [])
    },
    preFilterAnnotations() {
      return this.nonVisibleAnnotations.map(({ id, label }) => ({
        id,
        name: label,
        value: label,
      }))
    },
    isOrderingVisible() {
      return this.dragSourceId === this.visibleContainerId
    },
    isDraggingAttribute() {
      return this.dragSourceId === this.attributeContainerId
    },
    isDraggingAnnotation() {
      return this.dragSourceId === this.annotationContainerId
    },
    preventDrop() {
      let pointerEvents
      if (this.isOrderingVisible) pointerEvents = 'none'
      return { pointerEvents }
    },
    preventDropOfAnnotations() {
      return {
        ...((this.isDraggingAnnotation && { pointerEvents: 'none' }) ||
          this.preventDrop),
      }
    },
    preventDropOfAttributes() {
      return {
        ...((this.isDraggingAttribute && { pointerEvents: 'none' }) ||
          this.preventDrop),
      }
    },
    isAddingColumn() {
      return this.isDraggingAttribute || this.isDraggingAnnotation
    },
    selectedVisible() {
      return this.selectedVisibleIndices.map((i) => this.visibleColumns[i])
    },
    selectedAttributes() {
      return this.selectedAttributesIndices.map(
        (i) => this.nonVisibleAttributes[i],
      )
    },
    selectedAnnotations() {
      return this.selectedAnnotationsIndices.map(
        (i) => this.nonVisibleAnnotations[i],
      )
    },
    selectedProperties() {
      return [...this.selectedAttributes, ...this.selectedAnnotations]
    },
    mirrorSelected() {
      return this.isOrderingVisible
        ? this.selectedVisible
        : this.selectedProperties
    },
    mirrorElems() {
      return this.mirrorSelected.slice(0, this.maxMirrorElem).map((s) => {
        return this.createListItem(`${s.label}`)
      })
    },
    extraElems() {
      const extraElems = this.mirrorSelected.slice(this.maxMirrorElem)
      let extraTextContent = null
      if (extraElems.length === 1) {
        extraTextContent = `${extraElems[0].label}`
      } else if (extraElems.length > 1) {
        extraTextContent = `+ ${extraElems.length} others`
      }
      return extraTextContent && this.createListItem(extraTextContent)
    },
  },
  methods: {
    pluralize,
    helpText(item) {
      const text = item.help || item.description
      return text && text !== 'Select one.' ? text : null
    },
    moveVisibleColumns(fromIndex, toIndex) {
      const indices = this.dragSelection
        ? this.selectedVisibleIndices
        : [fromIndex]
      this.$emit('move-columns', {
        fromIndices: indices,
        toIndex,
      })
    },
    computeAddColumns(fromContainerId, fromIndex, toIndex) {
      let idsToAdd = []
      if (this.dragSelection) {
        idsToAdd = [
          ...this.selectedAttributesIndices.map(
            (i) => this.nonVisibleAttributes[i].id,
          ),
          ...this.selectedAnnotationsIndices.map(
            (i) => this.nonVisibleAnnotations[i].id,
          ),
        ]
      } else {
        const props =
          fromContainerId === this.attributeContainerId
            ? this.nonVisibleAttributes
            : this.nonVisibleAnnotations
        idsToAdd = [props[fromIndex].id]
      }
      this.addColumns(
        idsToAdd,
        toIndex,
        fromContainerId === this.attributeContainerId
          ? 'attributes'
          : 'annotations',
      )
    },
    addColumns(idsToAdd, index, container) {
      this.$emit('add-columns', { ids: idsToAdd, toIndex: index })
      this.$nextTick(() => container && this.reinitFilter(container))
    },
    reinitFilter(container) {
      if (container) {
        if (
          container === 'attributes' &&
          this.filteredAttributeIds.length === 0
        ) {
          this.attrAddReact++
        } else if (
          container === 'annotations' &&
          this.filteredAttributeIds.length === 0
        ) {
          this.annAddReact++
        }
      }
    },
    createListItem(textContent, className) {
      const classes = ['li-item', className]
      const li = document.createElement('li')
      classes.forEach((c) => li.classList.add(c))
      li.textContent = textContent
      return li
    },
    modifyMirror() {
      const mirrorElem = document.querySelector('li.draggable-mirror')
      mirrorElem.classList.add('drag-selection')
      mirrorElem.firstChild.style.height = '0'
      mirrorElem.firstChild.style.visibility = 'hidden'
      const mirror = document.createElement('ul')
      mirror.style['list-type'] = 'none'
      mirror.classList.add('sortable-items')
      this.mirrorElems.forEach((e) => mirror.appendChild(e))
      if (this.extraElems) mirror.appendChild(this.extraElems)
      mirrorElem.appendChild(mirror)
      this.updateMirror = true
    },
    modifyDragSource() {
      const dragSourceElem = document.querySelector(
        'li.draggable-source--is-dragging',
      )
      dragSourceElem.style.visibility = 'hidden'
      dragSourceElem.style.margin = '1.5rem'
    },
    restoreDraggedSourcePosition(parent) {
      const originalElement = parent.querySelector('li.draggable--original')
      const dragging = document.querySelector('.draggable-source--is-dragging')
      parent.insertBefore(dragging, originalElement)
    },
    setupSortable() {
      this.sortable = new Sortable(
        document.querySelectorAll('ul.sortable-items'),
        {
          draggable: 'li.li-item.sortable',
          handle: 'li.li-item .draggable-handle',
          distance: 15,
          mirror: {
            appendTo: 'body',
            constrainDimensions: true,
          },
        },
      )
      this.sortable.on('drag:start', (ev) => {
        if (this.clickEvent) {
          // Cancel select row
          this.clickEvent.preventDefault()
          this.clickEvent = null
        }
        this.onDragStart(ev.data)
        const { originalSource } = ev.data
        this.dragSelection = this.isOrderingVisible
          ? this.selectedVisibleIndices.length > 0
          : this.selectedProperties.length > 0

        if (this.dragSelection) {
          originalSource.style.display = 'block' // HERE DON'T HIDE THE ORIGINAL
        }
      })
      this.sortable.on('sortable:sort', (ev) => {
        const { currentIndex, dragEvent, source } = ev.data
        const { overContainer, sourceContainer } = dragEvent
        if (!this.dragSelection) {
          this.currentSortIndex = currentIndex
          source.classList.add('is-selected')
        }
        if (
          sourceContainer.id === overContainer.id &&
          (overContainer.id === this.attributeContainerId ||
            overContainer.id === this.annotationContainerId)
        ) {
          ev.cancel()
        }
      })
      this.sortable.on('sortable:stop', (ev) => {
        let { oldContainer, newContainer, oldIndex, newIndex } = ev.data
        const toVisibleContainer = newContainer.id === this.visibleContainerId
        if (this.isOrderingVisible && toVisibleContainer === true) {
          if (this.dragSelection) {
            this.restoreDraggedSourcePosition(
              document.getElementById(this.visibleContainerId),
            )
            // fix index...
            if (newIndex > oldIndex) newIndex++
          }
          this.moveVisibleColumns(oldIndex, newIndex)
        } else if (toVisibleContainer === true) {
          this.computeAddColumns(oldContainer.id, oldIndex, newIndex)
        }
        this.onDragStop()
      })
      this.sortable.on('drag:over:container', (ev) => {
        const { source, originalSource, sourceContainer, overContainer } =
          ev.data
        if (
          !this.isOrderingVisible &&
          sourceContainer.id === overContainer.id
        ) {
          this.restoreDraggedSourcePosition(
            document.getElementById(overContainer.id),
          )
          source.style.display = 'none'
          originalSource.style.display = 'block'
        } else {
          source.style.display = 'block'
          this.modifyDragSource()
        }
      })
      this.sortable.on('mirror:attached', () => {
        this.updateMirror = false
      })
      this.sortable.on('mirror:move', (ev) => {
        if (this.dragSelection && this.updateMirror === false) {
          this.modifyMirror(ev)
        }
      })
    },
    onDragStart(data) {
      const { sourceContainer } = data
      this.dragSourceId = sourceContainer.id
    },
    onDragStop() {
      this.dragSourceId = null
      this.lostOriginalSource = false
      this.currentSortIndex = -1
    },
    destroySortable() {
      this.sortable.destroy()
      this.sortable = null
    },
  },
  mounted() {
    this.setupSortable()
  },
  beforeUnmount() {
    this.destroySortable()
  },
}
</script>

<style lang="scss" scoped>
.column-settings-panel h1.title {
  margin: 1rem;
  margin-right: 4rem;
  font-weight: 500;
  font-family: var(--font-heading);
}

.column-settings-panel .enlight-container {
  -moz-box-shadow: 0px 0px 20px var(--brand);
  box-shadow: 0px 0px 20px var(--brand);
}
</style>

<style lang="scss">
//cannot be scoped cuz mirror appended to body
.draggable-mirror {
  z-index: var(--z-drag);
  display: inline-block;
}

.draggable-mirror .icon,
.draggable-mirror .icons {
  visibility: hidden;
  width: 0;
}

ul.sortable-items li.li-item {
  user-select: none;
  border: 1px solid var(--color-border-default);
}

ul.sortable-items li.li-item .draggable-handle {
  cursor: grab;
}

ul.sortable-items li.li-item.is-selected {
  background: var(--brand-light);
}

body.draggable--is-dragging ul.sortable-items li.li-item {
  cursor: grabbing;
}

body.draggable--is-dragging > li.li-item li.li-item,
body.draggable--is-dragging li.li-item.draggable-mirror:not(.drag-selection) {
  padding: 0.2rem 0.5rem;
  background-color: var(--brand-light);
  border: 1px solid var(--brand);
}
</style>
